// Client for global search (/api/search): SKU, branch and supplier lookups plus the streamed AI search.
import { headers, parse } from "./classificationApi";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/search`;

export const KINDS = [
  { id: "all", label: "All" },
  { id: "sku", label: "SKU" },
  { id: "branch", label: "Branch" },
  { id: "supplier", label: "Supplier" },
];
export const KIND_LABEL = { sku: "SKU", branch: "Branch", supplier: "Supplier", engine: "Engine" };

export const findMatches = (q, kind, signal) =>
  fetch(`${BASE}?${new URLSearchParams({ q, kind, limit: 5 })}`, { headers: headers(null, false), signal }).then(parse);
export const recentAi = () => fetch(`${BASE}/ai/recent`, { headers: headers(null, false) }).then(parse);
export const savedAi = (id) => fetch(`${BASE}/ai/${id}`, { headers: headers(null, false) }).then(parse);

export async function downloadBrief(id) {
  const res = await fetch(`${BASE}/ai/${id}/pdf`, { headers: headers(null, false) });
  if (!res.ok) await parse(res);
  const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1] || "AI-Search-brief.pdf";
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Reads an SSE response: meta → token… → done / error.
export async function streamFrom(url, body, { onMeta, onToken, onDone, signal }) {
  const res = await fetch(url, { method: "POST", headers: headers(null, true), body: JSON.stringify(body), signal });
  if (!res.ok || !res.body) await parse(res);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let finished = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) !== -1) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = /^event: (.+)$/m.exec(chunk)?.[1];
      const raw = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!ev || raw === undefined) continue;
      const d = JSON.parse(raw);
      if (ev === "meta") onMeta?.(d);
      else if (ev === "token") onToken?.(d.text);
      else if (ev === "done") { finished = true; onDone?.(d); }
      else if (ev === "error") throw new Error(d.detail);
    }
  }
  if (!finished) throw new Error("The answer was interrupted. Please try again.");
}

const streamSse = (path, body, handlers) => streamFrom(`${BASE}${path}`, body, handlers);

export const streamAi = (body, handlers) => streamSse("/ai", body, handlers);
export const streamFollowUp = (id, q, handlers) => streamSse(`/ai/${id}/followup`, { q }, handlers);
export const SAMPLE = " (sample)";
export const isSample = (name) => typeof name === "string" && name.endsWith(SAMPLE);
export const plainName = (name) => (isSample(name) ? name.slice(0, -SAMPLE.length) : name);

// "SUMMARY/ANSWER: … / INSIGHT: … / RECOMMEND: HIGH - …" → sections (lines without a prefix stay in the current section).
export function parseAnswer(text) {
  const out = { summary: "", lines: [], insights: [], recs: [] };
  let sec = null;
  const lines = (text || "").split("\n");
  lines.forEach((raw, idx) => {
    let line = raw.trim();
    if (!line || (idx === lines.length - 1 && /^[A-Z]{1,9}$/.test(line))) return;
    const m = /^(SUMMARY|ANSWER|INSIGHTS?|RECOMMEND(?:ATIONS?)?)\s*:\s*(.*)$/i.exec(line);
    if (m) {
      sec = m[1].toUpperCase().startsWith("INSIGHT") ? "INSIGHT" : m[1].toUpperCase().startsWith("RECOMMEND") ? "RECOMMEND" : "SUMMARY";
      line = m[2].trim();
      if (!line) return;
    }
    if (!sec || sec === "SUMMARY") { out.summary = out.summary ? `${out.summary} ${line}` : line; out.lines.push(line); }
    else if (sec === "INSIGHT") out.insights.push(line);
    else {
      const p = /^(HIGH|MEDIUM|LOW)\s*[-–—:]\s*(.*)$/i.exec(line);
      out.recs.push(p ? { level: p[1].toUpperCase(), text: p[2] } : { level: null, text: line });
    }
  });
  return out;
}
